import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';
import { MasterAccountMemberClassification } from '@insio/shared-types';
import { DeclareMasterAccountDocument, DeclareMasterAccountPeopleDocument } from '@/graphql';
import { navigation } from '@/navigation';
import { ACCOUNT_ENTRY_ACKNOWLEDGED_KEY, MembershipSource, useAuth } from '@/features/auth';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { MATRIX_PAGE_SIZE, pageStateOf, useReadAllPages } from '@/hooks/use-read-all-pages';
import {
  MasterAccountDeclarationErrorReason,
  masterAccountDeclarationErrorReason
} from './master-account-declaration-error';

/** A direct member of the account other than the caller, to be classified on declaring. */
export interface DeclarePerson {
  id: string;
  displayName: string;
  email: string | null;
}

export interface DeclarePeopleOptions {
  /** People per request. */
  pageSize?: number;
  /** False reads only the first page; `morePeople` then tells whether the list goes on. */
  readAllPages?: boolean;
}

/** Pages that are never followed, for a caller that reads only the first one. */
const NO_FURTHER_PAGES = pageStateOf(undefined);

/** How a declaration attempt ended; `peopleChanged` means the members must be classified again. */
export type DeclareOutcome = 'declared' | 'peopleChanged' | 'failed';

/**
 * The people the declaration must classify and the declare action itself. People are read
 * only while `open`; on success the page hard-navigates into the new master account board.
 */
export function useDeclareMasterAccount(
  open: boolean,
  { pageSize = MATRIX_PAGE_SIZE, readAllPages = true }: DeclarePeopleOptions = {}
) {
  const t = useFeatureTranslation('systemSettings.organization');
  const { showError } = useToastHelpers();
  const { user } = useAuth();
  const [classifications, setClassifications] = useState<
    Readonly<Record<string, MasterAccountMemberClassification>>
  >({});
  // A failed follow-up page does not surface through the query's own `error`.
  const [pageError, setPageError] = useState<Error | undefined>(undefined);
  const onPageError = useCallback((error: Error) => setPageError(error), []);

  const { data, loading, error, fetchMore, refetch } = useQuery(
    DeclareMasterAccountPeopleDocument,
    {
      variables: { first: pageSize },
      fetchPolicy: 'network-only',
      skip: !open
    }
  );
  const { loadingMore, exhausted } = useReadAllPages(
    readAllPages ? pageStateOf(data?.accountUsersConnection) : NO_FURTHER_PAGES,
    fetchMore,
    onPageError
  );

  const [declareMasterAccountMutation, { loading: declaring }] = useMutation(
    DeclareMasterAccountDocument
  );
  // Stays set while the browser leaves for the board, so nothing can be sent twice.
  const [declared, setDeclared] = useState(false);

  // The backend classifies live DIRECT people: master-account-materialized rows and machine identities
  // are not the account's people, and the caller is never classified.
  const people: DeclarePerson[] = useMemo(
    () =>
      (data?.accountUsersConnection.edges ?? [])
        .map(edge => edge.node)
        .filter(
          node =>
            node.source === MembershipSource.DIRECT &&
            !node.isMachineIdentity &&
            node.id !== user?.id
        )
        .map(node => ({ id: node.id, displayName: node.displayName, email: node.email })),
    [data, user?.id]
  );

  const classificationOf = useCallback(
    (userId: string): MasterAccountMemberClassification =>
      classifications[userId] ?? MasterAccountMemberClassification.EMPLOYEE,
    [classifications]
  );

  const classify = useCallback(
    (userId: string, classification: MasterAccountMemberClassification) => {
      setClassifications(prev => ({ ...prev, [userId]: classification }));
    },
    []
  );

  const morePeople = data?.accountUsersConnection.pageInfo.hasNextPage ?? false;
  const peopleLoading = open && (loading || loadingMore);
  const peopleError = !!error || !!pageError || exhausted;
  const ready = open && !peopleLoading && !peopleError && !!data;

  const declare = useCallback(async (): Promise<DeclareOutcome> => {
    const members = people.map(person => ({
      userId: person.id,
      classification: classificationOf(person.id)
    }));
    try {
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      const { error: declareError } = await declareMasterAccountMutation({
        variables: { input: { members } }
      });
      if (declareError) throw declareError;
    } catch (e) {
      const reason = masterAccountDeclarationErrorReason(e);
      showError(
        reason ? t(`masterAccountErrors.${reason}`) : t('masterAccountSection.declareError')
      );
      // The members changed meanwhile: reload them so the next attempt classifies everyone.
      if (
        reason === MasterAccountDeclarationErrorReason.CLASSIFICATION_INVALID ||
        reason === MasterAccountDeclarationErrorReason.DECLARE_CONFLICT
      ) {
        setPageError(undefined);
        void refetch();
        return 'peopleChanged';
      }
      return 'failed';
    }

    setDeclared(true);
    // A hard navigation rebuilds layout, routes and cache for the new board; declaring already
    // chose where to land, so the account-selection screen is skipped.
    sessionStorage.setItem(ACCOUNT_ENTRY_ACKNOWLEDGED_KEY, 'true');
    window.location.href = navigation.dashboard();
    return 'declared';
  }, [people, classificationOf, declareMasterAccountMutation, showError, t, refetch]);

  return {
    people,
    morePeople,
    peopleLoading,
    peopleError,
    ready,
    classificationOf,
    classify,
    declare,
    declaring: declaring || declared
  };
}
